import { getFormattedDateValue, dateFormats } from 'utils/Dates';
import { BasicUserLoanData, DateRange } from './types';
import { LOAN_STATUS } from 'components/LoansAndAdvances/constants';
import { TAB_PENDING, TAB_COMPLETED } from './constants';

export const normalizeLoanDetail = <T>(data: T[] | { data?: T[] } | undefined): T | undefined =>
  Array.isArray(data) ? data[0] : (data as { data?: T[] } | undefined)?.data?.[0];

export const formatDate = (dateStr?: string | null) =>
  getFormattedDateValue({ date: dateStr, formatString: 'dd MMM yyyy', filler: '—' });

export const formatMonthYear = (dateStr?: string | null) =>
  getFormattedDateValue({ date: dateStr, formatString: 'MMMM yyyy', filler: '—' });

export const formatMonthYearShort = (dateStr?: string | null) =>
  getFormattedDateValue({ date: dateStr, formatString: 'MMM yyyy', filler: '—' });

export const formatTenure = (hireDate: string | null | undefined): string => {
  if (!hireDate) return '—';
  const start = new Date(hireDate);
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  if (months < 0) {
    years--;
    months += 12;
  }
  const doj = start.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
  });
  return `${years}y, ${months}m (DOJ - ${doj})`;
};

export const formatDateToYMD = (date: Date): string => {
  return getFormattedDateValue({ date, formatString: dateFormats.yearMonthDate });
};

export const parseDateString = (str: string): Date | null => {
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

export const getEmiEndMonth = (recoveryStartFrom?: string, tenure?: number): string => {
  if (!recoveryStartFrom || !tenure) return '—';
  const start = new Date(recoveryStartFrom);
  start.setMonth(start.getMonth() + tenure - 1);
  return formatMonthYearShort(start.toISOString());
};

export type EligibilityCheck = { label: string; passed: boolean };

export const getInitialTab = (search: string): string => {
  const params = new URLSearchParams(search);
  const statuses = params.getAll('status');
  if (
    statuses.includes(String(LOAN_STATUS.APPROVED)) ||
    statuses.includes(String(LOAN_STATUS.REJECTED))
  )
    return TAB_COMPLETED;
  return TAB_PENDING;
};

export const getInitialDateRange = (search: string): DateRange => {
  const params = new URLSearchParams(search);
  const from = params.get('created_from');
  const to = params.get('created_to');
  return { from: from ? parseDateString(from) : null, to: to ? parseDateString(to) : null };
};

export const computeEligibilityChecks = (
  basicUserData: BasicUserLoanData | null | undefined,
  monthlyEmi: number,
): EligibilityCheck[] => {
  if (!basicUserData) return [];

  const hireDate = basicUserData.hire_date ?? null;
  const monthsWithOrg = hireDate
    ? (() => {
        const start = new Date(hireDate);
        const now = new Date();
        return (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
      })()
    : null;
  const existingLoans = basicUserData.existing_loan_count ?? 0;
  const postEmiNetPay = (basicUserData.salary ?? 0) / 12 - monthlyEmi;

  return [
    {
      label:
        monthsWithOrg !== null
          ? `${monthsWithOrg >= 6 ? 'More than' : 'Less than'} 6 months with organization`
          : 'Tenure not available',
      passed: monthsWithOrg !== null && monthsWithOrg >= 6,
    },
    {
      label:
        existingLoans === 0 ? 'No active loans or advances' : `${existingLoans} active loan(s)`,
      passed: existingLoans === 0,
    },
    {
      label:
        postEmiNetPay > 0 ? 'Post EMI net pay is positive' : 'Post EMI net pay is not positive',
      passed: postEmiNetPay > 0,
    },
  ];
};
